import { Empty } from '@byecode/ui'
import type { ActorEvent, ImageInfo } from '@lighthouse/core'
import { EVENT_TYPE, IMAGE_SHOW_TYPE, type ImageBlockAbstract, type ImageSwiperItem, ImageVariableValue } from '@lighthouse/core'
import {
    FilePreviewer,
    flexMinSizeStyle,
    getCursorStyle,
    getImageFullUrlInApplication,
    getWithScopeIdAndRecordIdAndColumnId,
    RATIO_OPTIONS,
    useActionRunningLoadings,
    useApplicationContext
} from '@lighthouse/shared'
import { useBreakpoint } from '@lighthouse/tools'
import { find } from 'rambda'
import React, { memo, useCallback, useMemo } from 'react'
import { useSetState } from 'react-use'

import { useContainerBlockContext, useCustomViewBlockContext, useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { Carousel } from './Carousel'
import { Grid } from './Grid'
import { List } from './List'
import * as SC from './styles'

interface ImageBlockProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onClick'> {
    blockData: ImageBlockAbstract
    showEmpty?: boolean
    value: ImageSwiperItem[]
    onClick?: (link: string) => void
    onActionTrigger?: (id: string, params: ActorEvent, options?: { imageInfo?: ImageInfo }) => Promise<void>
    onBlockChange?: (values: ImageBlockAbstract, origin: ImageBlockAbstract) => void
}

interface State {
    opened: boolean
    previewFileIndex: number
}

const ImageBlock: React.FC<ImageBlockProps> = ({
    blockData,
    showEmpty = false,
    value,
    onClick,
    onActionTrigger,
    onBlockChange,
    ...rest
}) => {
    const { config, id, title } = blockData
    const {
        carousel,
        fitType,
        align,
        radius,
        ratio,
        shape,
        gridGap,
        gridItemWidth,
        gridShowCount,
        listItemHeight,
        size,
        showType = IMAGE_SHOW_TYPE.single,
        direction,
        swipeSpeed = 2,
        autoPlay = false,
        breakPoint,
        cursor,
        compress,
        isPreview,
        background,
        eventList,
        anchor,
        noLazy = false
    } = config
    // const { size } = breakPoint || {}
    // const {overflow} = size || {}
    const overflow = breakPoint?.size?.overflow || 'visible'
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const isSinglePicture = showType === IMAGE_SHOW_TYPE.single
    const isGridPicture = showType === IMAGE_SHOW_TYPE.grid
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, scope, record?.id])
    const [{ opened, previewFileIndex }, setState] = useSetState<State>({
        opened: false,
        previewFileIndex: 0
    })
    const { appId } = useApplicationContext()
    const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
    const { ref: containRef, width: boxWidth, height: boxHeight } = useBreakpoint()

    const aspectRatio = useMemo(() => {
        const ratioOption = find(item => item.value === ratio, RATIO_OPTIONS)
        if (!ratioOption || ratioOption?.value === '0') {
            return 'auto'
        }
        return ratioOption.label.replace(':', '/')
    }, [ratio])
    const fileList = useMemo(() => {
        return value.map(item => ({
            ...item,
            url: getImageFullUrlInApplication(appId, item.url)
        }))
    }, [appId, value])

    const isEmpty = useMemo(() => value.length === 0 && showEmpty, [showEmpty, value.length])

    const alignStyles = useMemo(() => {
        switch (align) {
            case 'left': {
                return { marginRight: 'auto' }
            }
            case 'right': {
                return { marginLeft: 'auto' }
            }
            default: {
                return { margin: '0 auto' }
            }
        }
    }, [align])

    const clickEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.CLICK), [eventList])

    const handImageClick = useCallback(
        (ev: React.MouseEvent, imageInfo: ImageInfo) => {
            const { [ImageVariableValue.IMAGE_INDEX]: index } = imageInfo
            if (isPreview) {
                setState({ opened: true, previewFileIndex: index })
            }
            if (!clickEvent) {
                return
            }
            handleActionTriggerWithLoading({
                id: uniqueId,
                actorEvent: clickEvent,
                trigger: onActionTrigger ? (id, actorEvent) => onActionTrigger(id, actorEvent, { imageInfo }) : undefined
            })
        },
        [isPreview, handleActionTriggerWithLoading, uniqueId, clickEvent, onActionTrigger, setState]
    )

    const imageExtraStyles = useMemo<React.CSSProperties>(() => {
        if (loadings[uniqueId]) {
            return { cursor: 'not-allowed' }
        }
        return {}
    }, [uniqueId, loadings])

    const cursorStyle = useMemo(() => getCursorStyle(appId || '', cursor), [appId, cursor])
    const hasCustomCursor = !!cursorStyle.cursor

    const imageContent = useMemo(() => {
        if (isSinglePicture && carousel) {
            return (
                <Carousel
                    blockId={id}
                    noLazy={noLazy}
                    value={value}
                    autoPlay={autoPlay}
                    swipeSpeed={swipeSpeed}
                    fitType={fitType}
                    appId={appId}
                    background={background}
                    aspectRatio={aspectRatio}
                    shape={shape}
                    radius={radius}
                    width={boxWidth}
                    compress={compress}
                    isClickEvent={!!clickEvent}
                    handImageClick={handImageClick}
                    hasCustomCursor={hasCustomCursor}
                />
            )
        }

        if (isGridPicture) {
            return (
                <Grid
                    value={value}
                    noLazy={noLazy}
                    gridItemWidth={gridItemWidth}
                    gridShowCount={gridShowCount}
                    gridGap={gridGap}
                    radius={radius}
                    width={boxWidth}
                    appId={appId}
                    background={background}
                    compress={compress}
                    isClickEvent={!!clickEvent}
                    handImageClick={handImageClick}
                    hasCustomCursor={hasCustomCursor}
                />
            )
        }

        return (
            <List
                noLazy={noLazy}
                gridGap={gridGap}
                direction={direction}
                value={value}
                isSinglePicture={isSinglePicture}
                shape={shape}
                aspectRatio={aspectRatio}
                radius={radius}
                fitType={fitType}
                appId={appId}
                background={background}
                listItemHeight={listItemHeight}
                width={boxWidth}
                height={boxHeight}
                compress={compress}
                overflow={overflow}
                isClickEvent={!!clickEvent}
                handImageClick={handImageClick}
                hasCustomCursor={hasCustomCursor}
            />
        )
    }, [
        isSinglePicture,
        carousel,
        isGridPicture,
        noLazy,
        gridGap,
        direction,
        value,
        shape,
        aspectRatio,
        radius,
        fitType,
        appId,
        background,
        listItemHeight,
        boxWidth,
        boxHeight,
        compress,
        overflow,
        clickEvent,
        handImageClick,
        hasCustomCursor,
        id,
        autoPlay,
        swipeSpeed,
        gridItemWidth,
        gridShowCount
    ])

    return (
        <SC.ImageContainer
            blockId={id}
            blockName={title}
            animation={breakPoint.animation}
            id={effectiveAnchorId}
            data-block-id={id}
            ref={containRef}
            {...rest}
            style={{ ...flexMinSizeStyle, ...rest.style, ...cursorStyle }}
        >
            {isEmpty ? (
                <Empty
                    styles={{
                        root: {
                            width: '100%',
                            // height: containerWidth / 4,
                            aspectRatio: '4 / 1',
                            backgroundColor: 'var(--color-gray-50)',
                            border: '1px solid var(--color-gray-200)',
                            borderRadius: 'inherit'
                        }
                    }}
                    icon="BlockImage"
                    description=""
                />
            ) : (
                value.length > 0 && (
                    <SC.ImageContent style={{ ...alignStyles, ...imageExtraStyles, width: '100%' }}>{imageContent}</SC.ImageContent>
                )
            )}
            <FilePreviewer
                opened={opened}
                defaultIndex={previewFileIndex}
                onClose={() => setState({ opened: false })}
                fileList={fileList}
            />
        </SC.ImageContainer>
    )
}

export default memo(ImageBlock)
